import { msg } from "@lit/localize";

/**
 * Die Arten von Teilwahlen — Beschriftung und Reihenfolge.
 *
 * Aufgelöst wird ausschließlich über den `art`-Slug aus den Daten, nie über
 * den Wortlaut: „Wahl der Bezirksvertretung (Ehrenfeld)" enthält den
 * Bezirksnamen, wäre also nur durch Zerschneiden zu verarbeiten. Der Slug ist
 * der Vertrag, die Tabelle hier ist die Anzeige.
 *
 * KURZE BESCHRIFTUNG, ohne den Wortlaut zu ersetzen: Die Verweiszeile ist ein
 * Bedienelement und muss auf ein Telefon passen; der volle Wortlaut der Quelle
 * steht weiterhin in der Kopfzeile über dem Ergebnis.
 *
 * Unbekannte Arten bekommen keinen Eintrag — die Anzeige lässt sie dann weg,
 * statt einen Link ohne belastbaren Schlüssel zu bauen (so zugesichert von der
 * Pipeline, dort meldet die Inventur sie).
 */

/**
 * Reihenfolge der Verweiszeile, mit Kriterium: erst das Gremium, dann die
 * Person; erster Wahlgang vor Stichwahl.
 *
 * Nicht die gelieferte Reihenfolge — die ist alphabetisch nach dem Wortlaut
 * (`rat`, `ob-stichwahl`, `bezirksvertretung`, `ob`) und stellte die Stichwahl
 * vor die Wahl, deren Stichentscheid sie ist.
 */
export const TEILWAHL_ARTEN = [
  "rat",
  "bezirksvertretung",
  "ob",
  "ob-stichwahl",
  "landtag-erststimme",
  "landtag-zweitstimme",
  "bundestag-erststimme",
  "bundestag-zweitstimme",
  "europa",
] as const;

/**
 * Die Arten der KOMMUNALWAHL — was in Köln selbst gewählt wird.
 *
 * Eigene Liste, weil `TEILWAHL_ARTEN` drei Aufgaben hatte: Reihenfolge,
 * „kenne ich die Art?" und „welche Karten zeigt die Übersicht?". Mit dem
 * Bundestag und Europa fallen die auseinander — beschriften wollen wir sie,
 * als kommunale Wahl zeigen nicht.
 */
export const KOMMUNALE_ARTEN = ["rat", "bezirksvertretung", "ob", "ob-stichwahl"] as const;

/**
 * DIE EBENE JE ART — an einer Stelle.
 *
 * Sie beantwortet die Frage, die das Kapitel gliedert: Wird hier gewählt, oder
 * wählt Köln hier mit? Die Übersicht gruppiert danach; später trennt die
 * Verweiszeile der Ortsblöcke danach („hier gewählt" / „hier mitgewählt") und
 * die Detailseite ordnet ihre Sprungziele danach. Stünde sie nicht hier,
 * müsste jede dieser Stellen die Zugehörigkeit selbst erraten.
 */
export type WahlEbene = "stadt" | "land" | "bund" | "europa";

/** Von nah nach fern — die Ordnung der Gruppen auf der Übersicht. */
export const WAHL_EBENEN = ["stadt", "land", "bund", "europa"] as const;

const EBENE: Record<string, WahlEbene> = {
  rat: "stadt",
  bezirksvertretung: "stadt",
  ob: "stadt",
  "ob-stichwahl": "stadt",
  "landtag-erststimme": "land",
  "landtag-zweitstimme": "land",
  "bundestag-erststimme": "bund",
  "bundestag-zweitstimme": "bund",
  europa: "europa",
};

/** Ebene einer Art; unbekannt = undefined (dann zeigt die Übersicht sie nicht). */
export function teilwahlEbene(art: string | undefined): WahlEbene | undefined {
  return art ? EBENE[art] : undefined;
}

/**
 * WELCHE ORTSEBENEN EINE ART KENNT — die zweite Tabelle neben der Ebene.
 *
 * Nicht jede Wahl gibt es überall: Die Bezirksvertretung wird je Bezirk
 * gewählt und hat keinen stadtweiten Wert; die Landtagswahl veröffentlicht das
 * Land NUR je Wahlkreis und (gerechnet) für die Stadt — Bezirk und Stadtteil
 * gibt es dort nicht und wird es nicht geben.
 *
 * Daraus zieht die Auswahlleiste, welche Ortsfelder sie zeigt, und der
 * Artwechsel, ob er den Ort mitnehmen darf. Ohne diese Tabelle stünde beides
 * als Bedingung im Code — die BV-Ausnahme war genau so eine.
 */
export type OrtsEbene = "gesamtstadt" | "stadtbezirk" | "stadtteil";

const ORTE: Record<string, readonly OrtsEbene[]> = {
  rat: ["gesamtstadt", "stadtbezirk", "stadtteil"],
  ob: ["gesamtstadt", "stadtbezirk", "stadtteil"],
  "ob-stichwahl": ["gesamtstadt", "stadtbezirk", "stadtteil"],
  // Kein stadtweiter Wert: Jeder Bezirk wählt seine eigene Vertretung.
  bezirksvertretung: ["stadtbezirk", "stadtteil"],
  // Nur die Stadt; der Wahlkreis ist eine eigene Achse und noch nicht gebaut.
  "landtag-erststimme": ["gesamtstadt"],
  "landtag-zweitstimme": ["gesamtstadt"],
  "bundestag-erststimme": ["gesamtstadt", "stadtbezirk", "stadtteil"],
  "bundestag-zweitstimme": ["gesamtstadt", "stadtbezirk", "stadtteil"],
  europa: ["gesamtstadt", "stadtbezirk", "stadtteil"],
};

/**
 * Der Name einer Ebene — die Überschrift ihrer Gruppe.
 *
 * Im Auswahlfeld nennt sie die WAHL, denn die Einträge darunter sind ihre
 * Stimmen: „Erststimme" steht bei Landtag und Bundestag gleichermaßen, erst
 * die Gruppe macht sie unterscheidbar.
 */
export function ebeneName(ebene: WahlEbene | undefined): string | undefined {
  switch (ebene) {
    case "stadt":
      return msg("In Köln gewählt", { id: "app.wahl.auswahl.gruppe.stadt" });
    case "land":
      return msg("Landtagswahl", { id: "app.wahlebene.name.land" });
    case "bund":
      return msg("Bundestagswahl", { id: "app.wahlebene.name.bund" });
    case "europa":
      return msg("Europawahl", { id: "app.wahlebene.name.europa" });
    default:
      return undefined;
  }
}

/** Kennt diese Art diese Ortsebene? Unbekannte Art: alles erlauben, nichts raten. */
export function artKenntOrt(art: string | undefined, ort: OrtsEbene): boolean {
  const erlaubt = art ? ORTE[art] : undefined;
  return erlaubt ? erlaubt.includes(ort) : true;
}

const BESCHRIFTUNG: Record<string, () => string> = {
  rat: () => msg("Ratswahl", { id: "app.teilwahl.rat" }),
  bezirksvertretung: () => msg("Bezirksvertretung", { id: "app.teilwahl.bv" }),
  ob: () => msg("OB-Wahl", { id: "app.teilwahl.ob" }),
  "ob-stichwahl": () => msg("OB-Stichwahl", { id: "app.teilwahl.obStichwahl" }),
  "landtag-erststimme": () => msg("Erststimme", { id: "app.teilwahl.ltwErst" }),
  "landtag-zweitstimme": () => msg("Zweitstimme", { id: "app.teilwahl.ltwZweit" }),
  /*
   * Beim Bundestag nur der Stimmentyp: Der Name der Wahl steht in der
   * Kopfzeile darüber, und die Verweiszeile muss auf ein Telefon passen.
   */
  "bundestag-erststimme": () => msg("Erststimme", { id: "app.teilwahl.btwErst" }),
  "bundestag-zweitstimme": () => msg("Zweitstimme", { id: "app.teilwahl.btwZweit" }),
  europa: () => msg("Europawahl", { id: "app.teilwahl.europa" }),
};

/** Kurze Beschriftung zu einem Art-Slug; unbekannt = undefined. */
export function teilwahlBeschriftung(art: string | undefined): string | undefined {
  return art ? BESCHRIFTUNG[art]?.() : undefined;
}

/** Position in der Lesereihenfolge; Unbekanntes hinten. */
export function teilwahlRang(art: string | undefined): number {
  const i = art ? TEILWAHL_ARTEN.indexOf(art as (typeof TEILWAHL_ARTEN)[number]) : -1;
  return i === -1 ? TEILWAHL_ARTEN.length : i;
}

/**
 * Die ausgeschriebene Bezeichnung — für Titel und Karten, wo Platz ist.
 * „OB-Wahl" ist die Verweiszeile, „Oberbürgermeisterwahl" die Überschrift.
 */
const TITEL: Record<string, () => string> = {
  rat: () => msg("Ratswahl", { id: "app.teilwahl.titel.rat" }),
  bezirksvertretung: () => msg("Bezirksvertretungen", { id: "app.teilwahl.titel.bv" }),
  ob: () => msg("Oberbürgermeisterwahl", { id: "app.teilwahl.titel.ob" }),
  "ob-stichwahl": () => msg("Stichwahl", { id: "app.teilwahl.titel.obStichwahl" }),
  "landtag-erststimme": () =>
    msg("Landtagswahl · Erststimme", { id: "app.teilwahl.titel.ltwErst" }),
  "landtag-zweitstimme": () =>
    msg("Landtagswahl · Zweitstimme", { id: "app.teilwahl.titel.ltwZweit" }),
  "bundestag-erststimme": () =>
    msg("Bundestagswahl · Erststimme", { id: "app.teilwahl.titel.btwErst" }),
  "bundestag-zweitstimme": () =>
    msg("Bundestagswahl · Zweitstimme", { id: "app.teilwahl.titel.btwZweit" }),
  europa: () => msg("Europawahl", { id: "app.teilwahl.titel.europa" }),
};

export function teilwahlTitel(art: string | undefined): string | undefined {
  return art ? TITEL[art]?.() : undefined;
}

/**
 * Ein Satz je Art: was gewählt wird und was es bestimmt — kuratiert, ohne
 * Wertung (Fachkonzept 3.3 B). Sitzzahlen und Bezirkszahl stehen NICHT hier,
 * sondern kommen aus den Daten; der Satz nennt nur, was Landesrecht ist.
 */
const KURZTEXT: Record<string, () => string> = {
  rat: () =>
    msg(
      "Der Rat ist das Hauptorgan der Stadt. Er wird für fünf Jahre gewählt und entscheidet über alles, was die ganze Stadt betrifft.",
      { id: "app.teilwahl.kurz.rat" },
    ),
  bezirksvertretung: () =>
    msg(
      "Je Stadtbezirk eine Bezirksvertretung, mit eigenem Stimmzettel gewählt. Sie entscheidet über die Angelegenheiten ihres Bezirks.",
      { id: "app.teilwahl.kurz.bv" },
    ),
  ob: () =>
    msg(
      "Die Oberbürgermeisterin oder der Oberbürgermeister leitet die Verwaltung und führt den Vorsitz im Rat — direkt gewählt, für fünf Jahre.",
      { id: "app.teilwahl.kurz.ob" },
    ),
  "ob-stichwahl": () =>
    msg(
      "Erreicht im ersten Wahlgang niemand mehr als die Hälfte der Stimmen, entscheidet zwei Wochen später eine Stichwahl zwischen den beiden Bestplatzierten.",
      { id: "app.teilwahl.kurz.obStichwahl" },
    ),
  /*
   * Bund und Europa: Hier wird nicht über Köln entschieden, hier wählt Köln
   * mit. Der Satz sagt deshalb, WAS die Stimme bewirkt — beim Bundestag zwei
   * Stimmen mit zwei verschiedenen Wirkungen, das ist der Kern.
   */
  "bundestag-erststimme": () =>
    msg(
      "Mit der Erststimme wird eine Person aus dem eigenen Wahlkreis gewählt. Köln liegt in vier Wahlkreisen.",
      { id: "app.teilwahl.kurz.btwErst" },
    ),
  "bundestag-zweitstimme": () =>
    msg(
      "Die Zweitstimme gilt einer Landesliste und entscheidet, wie stark eine Partei im Bundestag vertreten ist.",
      { id: "app.teilwahl.kurz.btwZweit" },
    ),
  europa: () =>
    msg(
      "Mit einer Stimme wird eine Liste für das Europäische Parlament gewählt. Es gibt keine Sperrklausel.",
      { id: "app.teilwahl.kurz.europa" },
    ),
};

export function teilwahlKurztext(art: string | undefined): string | undefined {
  return art ? KURZTEXT[art]?.() : undefined;
}

/**
 * Ein Satz je EBENE — für die Karten, die eine ganze Wahl zeigen.
 *
 * Die Bundestagswahl hat zwei Stimmen mit zwei verschiedenen Wirkungen; das
 * ist ihr Kern und gehört in EINEN Satz. Der Satz je Art (oben) bleibt für die
 * Stelle, an der man wirklich vor einer der beiden steht.
 */
const EBENE_KURZTEXT: Record<string, () => string> = {
  land: () =>
    msg(
      "Zwei Stimmen: Die Erststimme wählt eine Person aus einem der sieben Kölner Landtagswahlkreise, die Zweitstimme eine Landesliste — sie entscheidet, wie stark eine Partei im Landtag ist.",
      { id: "app.wahlebene.kurz.land" },
    ),
  bund: () =>
    msg(
      "Zwei Stimmen: Die Erststimme wählt eine Person aus einem der vier Kölner Wahlkreise, die Zweitstimme eine Landesliste — sie entscheidet, wie stark eine Partei im Bundestag ist.",
      { id: "app.wahlebene.kurz.bund" },
    ),
  europa: () =>
    msg(
      "Eine Stimme für eine Liste zum Europäischen Parlament. Es gibt keine Sperrklausel, auch kleine Listen ziehen ein.",
      { id: "app.wahlebene.kurz.europa" },
    ),
};

export function ebeneKurztext(ebene: string | undefined): string | undefined {
  return ebene ? EBENE_KURZTEXT[ebene]?.() : undefined;
}
